import {
  CanadaPostServices,
  Carriers,
  isUpsCarrier,
  Service,
  UpsServices,
} from "../fulfillments/fulfillment-carriers-and-services";
import {
  findShipmentByTracker,
  FulfillmentGroup,
  FulfillmentGroupData,
  getFulfillmentGroupValue,
} from "../fulfillments/fulfillment-group";
import { OutboundParcel } from "../outbound-labels/parcel";
import { convertWeight, Weight, WeightUnit } from "../outbound-labels/util";
import { Team } from "../team";

export const CARRIERS_WITHOUT_INSURANCE_SUPPORT: Carriers[] = [
  Carriers.ETA_EXPRESS,
  Carriers.LANDMARK_GLOBAL,
] as const;

export function carrierSupportsInsurance(carrier: Carriers): boolean {
  return !CARRIERS_WITHOUT_INSURANCE_SUPPORT.includes(carrier);
}

export const DEFAULT_INSURANCE_INCLUDED_THRESHOLD = 100;
export const DEFAULT_INSURANCE_RATE_PERCENTAGE = 1;

const ONE_LB_IN_GRAMS = 453.592;

export const DEFAULT_INSURANCE_PACKAGE_WEIGHT: Weight = {
  unit: WeightUnit.POUND,
  value: 2,
};

export enum NO_INSURANCE_REASON {
  BULK_EDIT_NOT_ALLOWED = "BULK_EDIT_NOT_ALLOWED",
  CARRIER_UNSUPPORTED = "CARRIER_UNSUPPORTED",
  PACKAGE_PROTECTED = "PACKAGE_PROTECTED",
  NO_CARRIER_SELECTED = "NO_CARRIER_SELECTED",
  NO_PARCELS_SELECTED = "NO_PARCELS_SELECTED",
}

export const NO_INSURANCE_REASON_MESSAGES: Record<NO_INSURANCE_REASON, string> =
  {
    [NO_INSURANCE_REASON.BULK_EDIT_NOT_ALLOWED]:
      "Cannot bulk edit insurance for selected shipments. Insurance inclusion can differ between some carriers, service levels, and package weights",
    [NO_INSURANCE_REASON.CARRIER_UNSUPPORTED]:
      "Carrier does not support insurance",
    [NO_INSURANCE_REASON.PACKAGE_PROTECTED]: "Package is already protected",
    [NO_INSURANCE_REASON.NO_CARRIER_SELECTED]:
      "No carrier selected for this shipment",
    // This one should never get shown in the UI
    [NO_INSURANCE_REASON.NO_PARCELS_SELECTED]:
      "No parcels selected for this shipment",
  };

export enum PARCEL_THRESHOLD_REASON {
  DEFAULT_CARRIER_THRESHOLD = "DEFAULT_CARRIER_THRESHOLD",
  DHL_ECOMMERCE_UNDER_ONE_LB = "DHL_ECOMMERCE_UNDER_ONE_LB",
  UPS_GROUND_SAVER = "UPS_GROUND_SAVER",
  CANADA_POST_REGULAR_PARCEL = "CANADA_POST_REGULAR_PARCEL",
  CARRIER_UNSUPPORTED = "CARRIER_UNSUPPORTED",
}

export const PARCEL_THRESHOLD_NOTE_MESSAGES: Record<
  PARCEL_THRESHOLD_REASON,
  string
> = {
  DEFAULT_CARRIER_THRESHOLD: "", // No UI label
  CARRIER_UNSUPPORTED: "Carrier does not support insurance",
  DHL_ECOMMERCE_UNDER_ONE_LB:
    "DHL eCommerce: no insurance coverage for packages under 1 lb",
  UPS_GROUND_SAVER: "UPS Ground Saver: no coverage after USPS handoff",
  CANADA_POST_REGULAR_PARCEL:
    "Canada Post Regular Parcel: no insurance included by default",
};

/**
 * Returns the carrier/service-specific insurance coverage threshold and any
 * additional note that should be displayed in the UI.
 *
 * - UPS Ground Saver (SurePost): $50 while carried by UPS, no coverage after
 *   USPS handoff.
 * - DHL (packages under 1 lb): no insurance coverage.
 * - All other carriers/services: team-configured threshold or $100 default.
 */
export function getInsuranceAmountIncludedByDefaultThreshold({
  carrier,
  service,
  teamInsuranceIncludedThreshold,
  packageWeight,
}: {
  carrier: Carriers | null;
  service: Service | null;
  teamInsuranceIncludedThreshold: number;
  packageWeight: Weight | null | undefined;
}): { parcelInsuranceThreshold: number; reason: PARCEL_THRESHOLD_REASON } {
  // If we've set a custom threshold for the team and it's not the default, use it
  // For context, shopify covers $200 by default, so to win some deals we need to cover $200 by default
  if (
    teamInsuranceIncludedThreshold &&
    teamInsuranceIncludedThreshold !== DEFAULT_INSURANCE_INCLUDED_THRESHOLD
  ) {
    return {
      parcelInsuranceThreshold: teamInsuranceIncludedThreshold,
      reason: PARCEL_THRESHOLD_REASON.DEFAULT_CARRIER_THRESHOLD,
    };
  }

  if (!carrier || !service) {
    return {
      parcelInsuranceThreshold: DEFAULT_INSURANCE_INCLUDED_THRESHOLD,
      reason: PARCEL_THRESHOLD_REASON.DEFAULT_CARRIER_THRESHOLD,
    };
  }

  const { threshold, reason } = carrierServiceInsuranceThreshold({
    carrier,
    service,
    packageWeight: packageWeight ?? DEFAULT_INSURANCE_PACKAGE_WEIGHT,
  });
  return { parcelInsuranceThreshold: threshold, reason };
}

export function fulfillmentGroupInsuranceEligible({
  team,
  fulfillmentGroup,
}: {
  team: Team;
  fulfillmentGroup: FulfillmentGroupData;
}):
  | { insuranceEligible: true }
  | { insuranceEligible: false; reason: NO_INSURANCE_REASON } {
  // Allow buying insurance for normal package protection - not package protection plus
  if (team?.settings?.packageProtection?.packageProtectionPlusEnabled) {
    for (const order of fulfillmentGroup.orders) {
      if (order.packageProtected) {
        return {
          insuranceEligible: false,
          reason: NO_INSURANCE_REASON.PACKAGE_PROTECTED,
        };
      }
    }
  }

  const selectedCarrier =
    fulfillmentGroup.selectedShippingRate?.carrier ?? null;
  if (!selectedCarrier) {
    return {
      insuranceEligible: false,
      reason: NO_INSURANCE_REASON.NO_CARRIER_SELECTED,
    };
  }
  if (!carrierSupportsInsurance(selectedCarrier)) {
    return {
      insuranceEligible: false,
      reason: NO_INSURANCE_REASON.CARRIER_UNSUPPORTED,
    };
  }

  return { insuranceEligible: true };
}

// The threshold at which a carrier/service covers packages by default
export function carrierServiceInsuranceThreshold({
  carrier,
  service,
  packageWeight,
}: {
  carrier: Carriers;
  service: Service;
  packageWeight: Weight;
}): { threshold: number; reason: PARCEL_THRESHOLD_REASON } {
  if (!carrierSupportsInsurance(carrier)) {
    return {
      threshold: 0,
      reason: PARCEL_THRESHOLD_REASON.CARRIER_UNSUPPORTED,
    };
  }

  const packageWeightGrams = convertWeight(
    packageWeight,
    WeightUnit.GRAM,
  ).value;

  if (isDhlEcsUnderOneLb(carrier, packageWeightGrams)) {
    return {
      threshold: 0,
      reason: PARCEL_THRESHOLD_REASON.DHL_ECOMMERCE_UNDER_ONE_LB,
    };
  }

  if (isUpsGroundSaver(carrier, service)) {
    return { threshold: 50, reason: PARCEL_THRESHOLD_REASON.UPS_GROUND_SAVER };
  }

  if (isCanadaPostRegularParcel(carrier, service)) {
    return {
      threshold: 0,
      reason: PARCEL_THRESHOLD_REASON.CANADA_POST_REGULAR_PARCEL,
    };
  }

  return {
    threshold: DEFAULT_INSURANCE_INCLUDED_THRESHOLD,
    reason: PARCEL_THRESHOLD_REASON.DEFAULT_CARRIER_THRESHOLD,
  };
}

function isDhlEcsUnderOneLb(
  carrier: Carriers,
  packageWeightGrams: number | null,
): boolean {
  if (packageWeightGrams == null) {
    return false;
  }
  return carrier === Carriers.DHL_ECS && packageWeightGrams < ONE_LB_IN_GRAMS;
}

function isUpsGroundSaver(carrier: Carriers, service: Service): boolean {
  return (
    isUpsCarrier(carrier) &&
    (service === UpsServices.SurePostUnder1Lb ||
      service === UpsServices.SurePostOver1Lb)
  );
}

function isCanadaPostRegularParcel(
  carrier: Carriers,
  service: Service,
): boolean {
  return (
    carrier === Carriers.CANADA_POST &&
    service === CanadaPostServices.RegularParcel
  );
}

export const MIN_INSURANCE_COST = 0.7;

export function getParcelInsuranceCost({
  valueToInsure,
  teamRatePercentage,
  parcelInsuranceThreshold,
}: {
  valueToInsure: number;
  teamRatePercentage: number;
  parcelInsuranceThreshold: number;
}): number {
  // If the value of the package is less than the threshold at which insurance is included for free, don't charge anything
  if (valueToInsure <= parcelInsuranceThreshold) {
    return 0;
  }
  const rate = teamRatePercentage * 0.01;
  const costOfOnePackage = Math.max(rate * valueToInsure, MIN_INSURANCE_COST);
  return Math.round(costOfOnePackage * 100) / 100;
}

export function getTotalInsuranceCostForGroups({
  groups,
  teamRatePercentage,
  teamInsuranceIncludedThreshold,
}: {
  groups: readonly FulfillmentGroupData[];
  teamRatePercentage: number;
  teamInsuranceIncludedThreshold: number;
}): number {
  return groups.reduce((sum, group) => {
    return (
      sum +
      getTotalInsuranceCostForGroup({
        group,
        teamRatePercentage,
        teamInsuranceIncludedThreshold,
      })
    );
  }, 0);
}

export function getTotalInsuranceCostForGroup({
  group,
  teamRatePercentage,
  teamInsuranceIncludedThreshold,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  teamRatePercentage: number;
  teamInsuranceIncludedThreshold: number;
}): number {
  const costForGroup = (group.selectedParcels ?? []).reduce((sum, parcel) => {
    const { parcelInsuranceThreshold } =
      getInsuranceAmountIncludedByDefaultThreshold({
        carrier: group.selectedShippingRate?.carrier ?? null,
        service: group.selectedShippingRate?.service ?? null,
        teamInsuranceIncludedThreshold,
        packageWeight: parcel.parcelAndContentWeight,
      });
    const parcelValue = getInsuranceValueSingleParcel({
      group,
      parcel,
      parcelInsuranceThreshold,
    });
    const cost = getParcelInsuranceCost({
      teamRatePercentage,
      valueToInsure: parcelValue,
      parcelInsuranceThreshold,
    });
    return sum + cost;
  }, 0);

  return costForGroup;
}

export function getFulfillmentGroupInsuredValue({
  group,
  teamInsuranceIncludedThreshold,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  teamInsuranceIncludedThreshold: number;
}): number {
  const insuredValueForGroup = (group.selectedParcels ?? []).reduce(
    (sum, parcel) => {
      const { parcelInsuranceThreshold } =
        getInsuranceAmountIncludedByDefaultThreshold({
          carrier: group.selectedShippingRate?.carrier ?? null,
          service: group.selectedShippingRate?.service ?? null,
          teamInsuranceIncludedThreshold,
          packageWeight: parcel.parcelAndContentWeight,
        });
      const parcelValue = getInsuranceValueSingleParcel({
        group,
        parcel,
        parcelInsuranceThreshold,
      });
      return sum + parcelValue;
    },
    0,
  );

  return insuredValueForGroup;
}

export function getExplicitInsuredParcelsValue(
  fulfillmentGroup: FulfillmentGroup | FulfillmentGroupData,
): number {
  return (
    fulfillmentGroup.selectedParcels?.reduce(
      (sum, parcel) => sum + (parcel.insurance?.insuredAmount ?? 0),
      0,
    ) ?? 0
  );
}

// The default for every parcel is to insure the min between the value of the group and the default threshold
// So if Redo agrees to cover $200 on every package, and the group value is 150, we will cover 150 for every parcel
// But if the group value is 250, we will cover 200 for every parcel
// But if the user chooses to insure the parcel, we use that insured amount instead
export function getInsuranceValueSingleParcel({
  group,
  parcel,
  parcelInsuranceThreshold,
}: {
  group: FulfillmentGroup | FulfillmentGroupData;
  parcel: OutboundParcel;
  parcelInsuranceThreshold: number;
}): number {
  // Backwards compatability - if insuredAmount is unset but includeInsurance is set, it's insured fully
  if (parcel.insurance?.includeInsurance) {
    return getFulfillmentGroupValue(group);
  }

  const groupValue = getFulfillmentGroupValue(group);
  const defaultParcelCoverage = Math.min(groupValue, parcelInsuranceThreshold);
  const explicitInsuredAmount = parcel.insurance?.insuredAmount;
  return explicitInsuredAmount || defaultParcelCoverage;
}

export function getTotalValueToInsure(
  fulfillmentGroup: FulfillmentGroup | FulfillmentGroupData,
  insuranceIncludedThreshold: number,
) {
  if (
    fulfillmentGroup.selectedParcels?.length &&
    fulfillmentGroup.selectedParcels.length > 1
  ) {
    return fulfillmentGroup.selectedParcels.reduce((acc, parcel) => {
      const parcelInsuranceValue = getInsuranceValueSingleParcel({
        group: fulfillmentGroup,
        parcel,
        parcelInsuranceThreshold: insuranceIncludedThreshold,
      });
      return acc + parcelInsuranceValue;
    }, 0);
  }

  const firstParcel = fulfillmentGroup.selectedParcels?.[0];
  if (!firstParcel) {
    return 0;
  }

  return getInsuranceValueSingleParcel({
    group: fulfillmentGroup,
    parcel: firstParcel,
    parcelInsuranceThreshold: insuranceIncludedThreshold,
  });
}

export function getInsuredValue(
  fulfillmentGroup: FulfillmentGroup,
  trackingNumber: string,
  insuranceIncludedThreshold: number,
) {
  if (fulfillmentGroup.additionalShipments?.length) {
    const parcel = findShipmentByTracker(
      fulfillmentGroup,
      trackingNumber,
    )?.parcel;

    if (!parcel) {
      return 0;
    }

    const value = getInsuranceValueSingleParcel({
      group: fulfillmentGroup,
      parcel,
      parcelInsuranceThreshold: insuranceIncludedThreshold,
    });

    return value;
  }

  const firstParcel = fulfillmentGroup.selectedParcels?.[0];
  if (!firstParcel) {
    return 0;
  }

  return getInsuranceValueSingleParcel({
    group: fulfillmentGroup,
    parcel: firstParcel,
    parcelInsuranceThreshold: insuranceIncludedThreshold,
  });
}
